import { useMemo, useCallback } from 'react';
import { LanguageContext } from '@/components/LanguageState';
import readEnglishTranslations from '@/components/en/englishTranslationResource';

/**
 * EnLanguageProvider — supplies the SAME LanguageContext used by shared,
 * logic-carrying components (support FlowContainer, ContextPanel, PortalHeader),
 * but locked to English. This lets us reuse that shared logic inside the
 * hardcoded-English /en section with one source of truth for translations.
 *
 * The app's main LanguageProvider hard-forces Danish, so we can't reuse it for
 * the /en section — this thin wrapper provides `lang: 'en'` with the exact same
 * English translation table.
 */
export function EnLanguageProvider({ children }) {
  const t = useMemo(() => {
    return (keyPath, replacements = {}) => {
      const enTranslations = readEnglishTranslations();
      if (keyPath in enTranslations) {
        let value = enTranslations[keyPath];
        if (typeof value === 'string') {
          return Object.entries(replacements).reduce((str, [k, v]) => str.replace(`{${k}}`, v), value);
        }
        return value;
      }
      const keys = keyPath.split('.');
      let value = enTranslations;
      for (const key of keys) {
        if (value && typeof value === 'object' && key in value) value = value[key];
        else return keyPath;
      }
      if (typeof value === 'string') {
        return Object.entries(replacements).reduce((str, [k, v]) => str.replace(`{${k}}`, v), value);
      }
      return value;
    };
  }, []);

  const localePath = useCallback((path) => path, []);

  const value = useMemo(() => ({
    lang: 'en',
    t,
    localePath,
    switchToDanish: () => {},
    switchLanguage: () => {},
    isEnglish: true,
    isDanish: false,
    initialized: true,
  }), [t, localePath]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export default EnLanguageProvider;